import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { 
    LayoutDashboard, 
    Package, 
    Warehouse, 
    Store, 
    History, 
    Users,
    Search,
    ScanBarcode,
    BarChart3,
    TrendingUp,
    EyeOff,
    Eye,
    PackageOpen,
    Receipt,
    RefreshCw,
    Settings,
    Trash2,
    FileText,
    Wallet,
    Database,
    BookmarkCheck,
    ScrollText,
    ShoppingCart,
    Bell,
    X,
    Phone,
    MapPin,
    CheckCircle,
    LogOut,
    User,
    Activity,
    Globe,
    ClipboardCheck
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Format price with Romanian locale (comma as decimal separator)
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0';
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2).replace('.', ',');
};

// Version Context
export const VersionContext = createContext({
    currentVersion: 'v1',
    switchVersion: () => {}
});
export const useVersion = () => useContext(VersionContext);

// Safe Mode Context (hides marked customers)
export const SafeModeContext = createContext({
    safeMode: false,
    toggleSafeMode: () => {}
});

export const useSafeMode = () => useContext(SafeModeContext);

// Keep old exports for backward compatibility
export const PrivacyModeContext = SafeModeContext;
export const usePrivacyMode = useSafeMode;

// Refresh Context for updating sidebar values
export const RefreshContext = createContext({
    refreshStats: () => {}
});
export const useRefresh = () => useContext(RefreshContext);

// Navigation items for each version
const navItemsV1 = [
    { path: '/customers', icon: Users, label: 'Comenzi' },
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/products', icon: Package, label: 'Produse' },
    { path: '/depozit', icon: Warehouse, label: 'Depozit' },
    { path: '/store', icon: Store, label: 'Magazin' },
    { path: '/stock-general', icon: Package, label: 'Stock General' },
    { path: '/history', icon: History, label: 'Istoric' },
    { path: '/reports', icon: BarChart3, label: 'Rapoarte' },
    { path: '/expenses', icon: Receipt, label: 'Cheltuieli' },
];

const navItemsV2 = [
    { path: '/acasa', icon: LayoutDashboard, label: 'Acasă' },
    { path: '/control-clienti', icon: Users, label: 'Control Clienți' },
    { path: '/search', icon: Search, label: 'Căutare' },
    { path: '/receptie', icon: PackageOpen, label: 'Recepție Paleți' },
    { path: '/magazin-v2', icon: Store, label: 'Magazin' },
    { path: '/comenzi-v2', icon: Receipt, label: 'Comenzi' },
    { path: '/comenzi-virtuale', icon: ShoppingCart, label: 'Comenzi Neconfirmate' },
    { path: '/rezervari', icon: BookmarkCheck, label: 'Rezervări' },
    { path: '/depozit-v2', icon: Warehouse, label: 'Depozit' },
    { path: '/scrap-v2', icon: Trash2, label: 'SCRAP' },
    { path: '/istoric-paleti', icon: History, label: 'Istoric Paleți' },
    { path: '/istoric-miscari', icon: Activity, label: 'Istoric Mișcări' },
    { path: '/facturi', icon: FileText, label: 'Facturi' },
    { path: '/cheltuieli', icon: Wallet, label: 'Cheltuieli' },
    { path: '/rapoarte-v2', icon: BarChart3, label: 'Rapoarte' },
    { path: '/control-stock', icon: ClipboardCheck, label: 'Control Stock' },
    { path: '/web-oferte', icon: Globe, label: 'WEB Oferte' },
    { path: '/baza-date', icon: Database, label: 'Bază Date Produse' },
    { path: '/loguri', icon: ScrollText, label: 'Loguri' },
    { path: '/setari', icon: Settings, label: 'Setări' },
];

// V3 = V2 + TVA display (same pages, TVA calculations shown)
const navItemsV3 = [
    { path: '/acasa', icon: LayoutDashboard, label: 'Acasă' },
    { path: '/control-clienti', icon: Users, label: 'Control Clienți' },
    { path: '/search', icon: Search, label: 'Căutare' },
    { path: '/receptie', icon: PackageOpen, label: 'Recepție Paleți' },
    { path: '/magazin-v2', icon: Store, label: 'Magazin' },
    { path: '/comenzi-v2', icon: Receipt, label: 'Comenzi' },
    { path: '/comenzi-virtuale', icon: ShoppingCart, label: 'Comenzi Neconfirmate' },
    { path: '/rezervari', icon: BookmarkCheck, label: 'Rezervări' },
    { path: '/depozit-v2', icon: Warehouse, label: 'Depozit' },
    { path: '/scrap-v2', icon: Trash2, label: 'SCRAP' },
    { path: '/istoric-paleti', icon: History, label: 'Istoric Paleți' },
    { path: '/istoric-miscari', icon: Activity, label: 'Istoric Mișcări' },
    { path: '/facturi', icon: FileText, label: 'Facturi' },
    { path: '/cheltuieli', icon: Wallet, label: 'Cheltuieli' },
    { path: '/rapoarte-v2', icon: BarChart3, label: 'Rapoarte' },
    { path: '/control-stock', icon: ClipboardCheck, label: 'Control Stock' },
    { path: '/web-oferte', icon: Globe, label: 'WEB Oferte' },
    { path: '/baza-date', icon: Database, label: 'Bază Date Produse' },
    { path: '/loguri', icon: ScrollText, label: 'Loguri' },
    { path: '/setari', icon: Settings, label: 'Setări' },
];

export const Layout = ({ children, onScanClick, privacyMode, togglePrivacyMode }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [stockValue, setStockValue] = useState(0);
    const [ordersValue, setOrdersValue] = useState(0);
    const [euroRate, setEuroRate] = useState(5);
    
    // Delivery requests state
    const [deliveryRequests, setDeliveryRequests] = useState([]);
    const [showDeliveryPanel, setShowDeliveryPanel] = useState(false);
    
    // Handle logout
    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };
    
    // V2 Settings
    const [v2Settings, setV2Settings] = useState({
        show_comenzi_palet: true,
        show_version_switch: true,
        comenzi_palet_name: 'Comenzi din Palet',
        logo_url: '',
        full_logo_url: ''
    });
    
    // Version management
    const [currentVersion, setCurrentVersion] = useState(() => {
        const saved = localStorage.getItem('app_version');
        if (!saved) {
            // Set default to v2 in localStorage
            localStorage.setItem('app_version', 'v2');
            return 'v2';
        }
        return saved;
    });
    const [isVersionSwitching, setIsVersionSwitching] = useState(false);

    const switchVersion = async (version) => {
        if (version === currentVersion || isVersionSwitching) return;
        
        setIsVersionSwitching(true);
        try {
            await api.post(`/version/switch/${version}`);
            setCurrentVersion(version);
            localStorage.setItem('app_version', version);
            toast.success(`Versiune schimbată: ${version.toUpperCase()}`);
            // Redirect to appropriate home page
            if (version === 'v1') {
                window.location.href = '/dashboard';
            } else {
                window.location.href = '/acasa';
            }
        } catch (err) {
            console.error('Error switching version:', err);
            toast.error('Eroare la schimbarea versiunii');
        }
        setIsVersionSwitching(false);
    };

    // Sync version with backend on mount
    useEffect(() => {
        const syncVersion = async () => {
            const savedVersion = localStorage.getItem('app_version') || 'v2';
            try {
                await api.post(`/version/switch/${savedVersion}`);
            } catch (err) {
                console.error('Error syncing version:', err);
            }
        };
        syncVersion();
    }, []);
    
    // Load V2 settings
    const loadV2Settings = useCallback(async () => {
        try {
            const res = await api.get('/settings/v2');
            if (res.data) {
                setV2Settings({
                    show_comenzi_palet: res.data.show_comenzi_palet !== false,
                    show_version_switch: res.data.show_version_switch !== false,
                    comenzi_palet_name: res.data.comenzi_palet_name || 'Comenzi din Palet',
                    logo_url: res.data.logo_url || '',
                    full_logo_url: res.data.full_logo_url || ''
                });
            }
        } catch (err) {
            console.error('Error loading V2 settings:', err);
        }
    }, []);
    
    useEffect(() => {
        loadV2Settings();
        // Listen for settings update event
        const handleSettingsUpdate = () => loadV2Settings();
        window.addEventListener('settings-updated', handleSettingsUpdate);
        return () => window.removeEventListener('settings-updated', handleSettingsUpdate);
    }, [loadV2Settings]);

    const loadValues = useCallback(async () => {
        try {
            const [statsRes, settingsRes] = await Promise.all([
                api.get(`/stats/values?safe_mode=${privacyMode}`),
                api.get('/settings')
            ]);
            setStockValue(statsRes.data.stock_value);
            setOrdersValue(statsRes.data.orders_value);
            setEuroRate(settingsRes.data.euro_rate || 5);
        } catch (err) {
            console.error('Error loading values:', err);
        }
    }, [privacyMode]);

    useEffect(() => {
        loadValues();
        const interval = setInterval(loadValues, 30000);
        
        // Listen for quick sale complete event to refresh stats
        const handleQuickSaleComplete = () => {
            loadValues();
        };
        window.addEventListener('quicksale-complete', handleQuickSaleComplete);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('quicksale-complete', handleQuickSaleComplete);
        };
    }, [loadValues]);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({ refreshStats: loadValues }), [loadValues]);

    // Load delivery requests and modification requests
    const [modificationRequests, setModificationRequests] = useState([]);
    const [showModificationPanel, setShowModificationPanel] = useState(false);
    
    const loadDeliveryRequests = useCallback(async () => {
        try {
            const res = await api.get('/delivery-requests');
            setDeliveryRequests(res.data.requests || []);
        } catch (err) {
            console.error('Error loading delivery requests:', err);
        }
    }, []);
    
    const loadModificationRequests = useCallback(async () => {
        try {
            const res = await api.get('/modification-requests');
            setModificationRequests(res.data.requests || []);
        } catch (err) {
            console.error('Error loading modification requests:', err);
        }
    }, []);

    useEffect(() => {
        loadDeliveryRequests();
        loadModificationRequests();
        const interval = setInterval(() => {
            loadDeliveryRequests();
            loadModificationRequests();
        }, 60000); // Check every minute
        return () => clearInterval(interval);
    }, [loadDeliveryRequests, loadModificationRequests]);

    const handleMarkDone = async (customerId) => {
        try {
            await api.post(`/delivery-requests/${customerId}/mark-done`);
            toast.success('Solicitare procesată!');
            loadDeliveryRequests();
        } catch (err) {
            toast.error('Eroare');
        }
    };
    
    const handleMarkModificationDone = async (requestId) => {
        try {
            await api.post(`/modification-requests/${requestId}/mark-done`);
            toast.success('Cerere de modificare procesată!');
            loadModificationRequests();
        } catch (err) {
            toast.error('Eroare');
        }
    };
    
    // Get reason label
    const getReasonLabel = (reason, customReason) => {
        const reasons = {
            'nu_mai_doresc': 'Nu îl mai doresc',
            'in_plus': 'Este în plus',
            'nu_e_comanda_mea': 'Nu e comanda mea',
            'altele': customReason || 'Altele'
        };
        return reasons[reason] || reason;
    };

    return (
        <RefreshContext.Provider value={contextValue}>
        <div className="min-h-screen bg-[#F8F9FA]">
            {/* Desktop Sidebar */}
            <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-zinc-200 flex-col z-30">
                <div className="p-4 border-b border-zinc-200">
                    {/* Full Logo - if set */}
                    {v2Settings.full_logo_url && (
                        <div className="mb-3 flex justify-center">
                            <img 
                                src={v2Settings.full_logo_url} 
                                alt="Logo" 
                                className="max-h-16 object-contain"
                            />
                        </div>
                    )}
                    <h1 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        TOTH ALINA COMMERCE PFA
                    </h1>
                    <div className="text-xs text-zinc-500 mt-2 space-y-0.5">
                        <p>CIF: 54080688</p>
                        <p>Reg.com: F2026010134009</p>
                        <p>STR. 1, NR.21, SAT SÂNPAUL</p>
                        <p>COM. ŞOFRONEA, ARAD</p>
                        <p>Tel: 0741415463</p>
                        <div className="flex items-center gap-1 mt-1">
                            <span>IBAN: RO65RNCB0857185084040001</span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText('RO65RNCB0857185084040001');
                                }}
                                className="p-0.5 hover:bg-zinc-200 rounded text-zinc-400 hover:text-zinc-600"
                                title="Copiază IBAN"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Delivery Requests Notification */}
                {deliveryRequests.length > 0 && (
                    <div className="px-4 py-2 border-b border-zinc-200">
                        <button
                            onClick={() => setShowDeliveryPanel(true)}
                            className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 transition-all"
                        >
                            <span className="flex items-center gap-2 text-red-700 font-medium">
                                <Bell size={18} />
                                Solicitări pregătire
                            </span>
                            <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse">
                                {deliveryRequests.length}
                            </span>
                        </button>
                    </div>
                )}
                
                {/* Modification Requests Notification */}
                {modificationRequests.length > 0 && (
                    <div className="px-4 py-2 border-b border-zinc-200">
                        <button
                            onClick={() => setShowModificationPanel(true)}
                            className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-all"
                        >
                            <span className="flex items-center gap-2 text-amber-700 font-medium">
                                <Bell size={18} />
                                Cereri modificare
                            </span>
                            <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse">
                                {modificationRequests.length}
                            </span>
                        </button>
                    </div>
                )}
                
                {/* Version Switch - show only if enabled or in V1 */}
                {(v2Settings.show_version_switch || currentVersion === 'v1') && (
                    <div className="px-4 py-2 border-b border-zinc-200">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-500 font-medium">VERSIUNE</span>
                            <div className="flex bg-zinc-100 rounded-lg p-0.5">
                                <button
                                    onClick={() => switchVersion('v1')}
                                    disabled={isVersionSwitching}
                                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                                        currentVersion === 'v1'
                                            ? 'bg-violet-500 text-white shadow-sm'
                                            : 'text-zinc-600 hover:text-zinc-900'
                                    }`}
                                >
                                    V1
                                </button>
                                <button
                                    onClick={() => switchVersion('v2')}
                                    disabled={isVersionSwitching}
                                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                                        currentVersion === 'v2'
                                            ? 'bg-emerald-500 text-white shadow-sm'
                                            : 'text-zinc-600 hover:text-zinc-900'
                                    }`}
                                >
                                    V2
                                </button>
                                <button
                                    onClick={() => switchVersion('v3')}
                                    disabled={isVersionSwitching}
                                    className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                                        currentVersion === 'v3'
                                            ? 'bg-blue-500 text-white shadow-sm'
                                            : 'text-zinc-600 hover:text-zinc-900'
                                    }`}
                                >
                                    V3
                                </button>
                            </div>
                        </div>
                        {currentVersion === 'v3' && (
                            <div className="mt-1 text-xs text-blue-600 text-center">
                                TVA 21% activat
                            </div>
                        )}
                        {isVersionSwitching && (
                            <div className="flex items-center justify-center gap-2 mt-2 text-xs text-zinc-500">
                                <RefreshCw size={12} className="animate-spin" />
                                Se schimbă...
                            </div>
                        )}
                    </div>
                )}
                
                {/* Safe Mode Toggle - only show in V1 */}
                {currentVersion === 'v1' && (
                    <div className="px-4 py-3 border-b border-zinc-200">
                        <button
                            onClick={togglePrivacyMode}
                            data-testid="safe-mode-toggle"
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                                privacyMode 
                                    ? 'bg-red-50 border border-red-200 text-red-700' 
                                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                        >
                            <span className="flex items-center gap-2 font-medium">
                                {privacyMode ? <EyeOff size={18} /> : <Eye size={18} />}
                                {privacyMode ? 'Safe Mode ACTIV' : 'Mod Normal'}
                            </span>
                            <span className={`w-3 h-3 rounded-full ${privacyMode ? 'bg-red-500' : 'bg-green-500'}`}></span>
                        </button>
                    </div>
                )}
                
                {/* Status Values */}
                {/* Status Values - only show in V1 */}
                {currentVersion === 'v1' && (
                    <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-zinc-500 flex items-center gap-1">
                                <Warehouse size={12} />
                                Stock
                            </span>
                            <div className="text-right">
                                <span className="text-sm font-bold text-amber-600" data-testid="stock-value">
                                    {formatPrice(stockValue)} RON
                                </span>
                                <span className="text-xs text-zinc-400 ml-1">
                                    ({formatPrice(stockValue / euroRate)}€)
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-500 flex items-center gap-1">
                                <TrendingUp size={12} />
                                Comenzi
                            </span>
                            <div className="text-right">
                                <span className="text-sm font-bold text-violet-600" data-testid="orders-value">
                                    {formatPrice(ordersValue)} RON
                                </span>
                                <span className="text-xs text-zinc-400 ml-1">
                                    ({formatPrice(ordersValue / euroRate)}€)
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                
                <nav className="flex-1 p-4 overflow-y-auto">
                    <ul className="space-y-1">
                        {(currentVersion === 'v3' ? navItemsV3 : currentVersion === 'v2' ? navItemsV2 : navItemsV1).map(({ path, icon: Icon, label }) => (
                            <li key={path}>
                                <NavLink
                                    to={path}
                                    data-testid={`nav-${label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                                            isActive
                                                ? 'bg-zinc-900 text-white'
                                                : 'text-zinc-600 hover:bg-zinc-100'
                                        }`
                                    }
                                >
                                    <Icon size={20} strokeWidth={1.5} />
                                    <span className="font-medium">{label}</span>
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="p-4 border-t border-zinc-200 space-y-2">
                    <NavLink
                        to="/search"
                        data-testid="nav-search"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                                isActive
                                    ? 'bg-zinc-900 text-white'
                                    : 'text-zinc-600 hover:bg-zinc-100'
                            }`
                        }
                    >
                        <Search size={20} strokeWidth={1.5} />
                        <span className="font-medium">Căutare</span>
                    </NavLink>
                    
                    {/* User info and Logout */}
                    {user && (
                        <div className="pt-2 border-t border-zinc-100">
                            <div className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-500">
                                <User size={16} />
                                <span>{user.name || user.username}</span>
                            </div>
                            <button
                                onClick={handleLogout}
                                data-testid="logout-button"
                                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-red-600 hover:bg-red-50 w-full"
                            >
                                <LogOut size={20} strokeWidth={1.5} />
                                <span className="font-medium">Deconectare</span>
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {/* Mobile Bottom Navigation */}
            <nav className="md:hidden mobile-nav" style={{ bottom: '48px' }}>
                {/* Mobile status bar - only show in V1 */}
                {currentVersion === 'v1' && (
                    <div className="flex justify-between items-center px-4 py-1.5 bg-zinc-50 border-b border-zinc-100 text-xs">
                        <span className="text-amber-600 font-medium">
                            Stock: {formatPrice(stockValue)} <span className="text-zinc-400">({formatPrice(stockValue / euroRate)}€)</span>
                        </span>
                        <button
                            onClick={togglePrivacyMode}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded ${
                                privacyMode ? 'bg-red-100 text-red-600' : 'bg-zinc-200 text-zinc-600'
                            }`}
                        >
                            {privacyMode ? <EyeOff size={12} /> : <Eye size={12} />}
                            {privacyMode ? 'Safe' : 'Normal'}
                        </button>
                        <span className="text-violet-600 font-medium">
                            Comenzi: {formatPrice(ordersValue)} <span className="text-zinc-400">({formatPrice(ordersValue / euroRate)}€)</span>
                        </span>
                    </div>
                )}
                {/* Scrollable nav items */}
                <div className="flex overflow-x-auto gap-1 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {(currentVersion === 'v3' ? navItemsV3 : currentVersion === 'v2' ? navItemsV2 : navItemsV1).map(({ path, icon: Icon, label }) => (
                        <NavLink
                            key={path}
                            to={path}
                            data-testid={`mobile-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center p-2 rounded-lg min-w-[60px] ${
                                    isActive ? 'text-zinc-900 bg-zinc-100' : 'text-zinc-400'
                                }`
                            }
                        >
                            <Icon size={20} strokeWidth={1.5} />
                            <span className="text-[10px] mt-0.5 font-medium whitespace-nowrap">{label}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>
            
            {/* Black footer bar for mobile */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 h-12 bg-black flex items-center justify-between px-4 z-50">
                <div className="flex items-center">
                    {v2Settings.logo_url && (
                        <img 
                            src={v2Settings.logo_url} 
                            alt="Logo" 
                            className="h-8 w-8 object-contain mr-2"
                        />
                    )}
                    <span className="text-white text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Toth Commerce PFA
                    </span>
                </div>
                
                {/* Version Switch on Mobile */}
                {(v2Settings.show_version_switch || currentVersion === 'v1') && (
                    <div className="flex bg-zinc-800 rounded-lg p-0.5">
                        <button
                            onClick={() => switchVersion('v1')}
                            disabled={isVersionSwitching}
                            className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                                currentVersion === 'v1'
                                    ? 'bg-violet-500 text-white'
                                    : 'text-zinc-400 hover:text-white'
                            }`}
                        >
                            V1
                        </button>
                        <button
                            onClick={() => switchVersion('v2')}
                            disabled={isVersionSwitching}
                            className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                                currentVersion === 'v2'
                                    ? 'bg-emerald-500 text-white'
                                    : 'text-zinc-400 hover:text-white'
                            }`}
                        >
                            V2
                        </button>
                        <button
                            onClick={() => switchVersion('v3')}
                            disabled={isVersionSwitching}
                            className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                                currentVersion === 'v3'
                                    ? 'bg-blue-500 text-white'
                                    : 'text-zinc-400 hover:text-white'
                            }`}
                        >
                            V3
                        </button>
                    </div>
                )}
            </div>

            {/* Scan FAB Button - hide on receptie and comenzi-v2 pages */}
            {location.pathname !== '/receptie' && location.pathname !== '/comenzi-v2' && (
                <button
                    onClick={() => {
                        // For V2 pages, emit custom event
                        if (location.pathname === '/magazin-v2') {
                            window.dispatchEvent(new CustomEvent('v2-scan-magazin'));
                        } else if (location.pathname === '/scrap-v2') {
                            window.dispatchEvent(new CustomEvent('v2-scan-scrap'));
                        } else if (location.pathname === '/depozit-v2') {
                            window.dispatchEvent(new CustomEvent('v2-scan-depozit'));
                        } else if (location.pathname === '/acasa' || location.pathname === '/') {
                            // From Acasa, navigate to comenzi and trigger scan
                            window.location.href = '/comenzi-v2?scan=true';
                        } else {
                            onScanClick?.();
                        }
                    }}
                    data-testid="scan-fab-button"
                    className="scan-fab"
                    aria-label="Scanează cod bare"
                >
                    <ScanBarcode size={24} strokeWidth={1.5} />
                </button>
            )}

            {/* Main Content */}
            <main className="md:ml-64 min-h-screen pb-24 md:pb-0">
                <div className="p-4 md:p-8">
                    {children}
                </div>
            </main>

            {/* Delivery Requests Panel */}
            {showDeliveryPanel && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
                        <div className="bg-gradient-to-r from-red-500 to-rose-600 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-white font-bold text-lg flex items-center gap-2">
                                <Bell size={20} />
                                Solicitări Livrare ({deliveryRequests.length})
                            </h2>
                            <button
                                onClick={() => setShowDeliveryPanel(false)}
                                className="text-white/80 hover:text-white p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            {deliveryRequests.length === 0 ? (
                                <p className="text-center text-zinc-500 py-8">Nu există solicitări noi</p>
                            ) : (
                                <div className="space-y-3">
                                    {deliveryRequests.map((req) => (
                                        <div key={req.customer_id} className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <h3 className="font-bold text-zinc-800">
                                                        {req.name || `Client ${req.customer_id}`}
                                                    </h3>
                                                    <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                                                        <Phone size={14} />
                                                        {req.phone || 'Fără telefon'}
                                                    </div>
                                                    {req.box_number && (
                                                        <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                                                            <MapPin size={14} />
                                                            Cutia {req.box_number}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-lg font-bold text-emerald-600">
                                                    {req.grand_total?.toFixed(2)} RON
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-zinc-400 mt-2">
                                                <span>
                                                    {req.reservation_items > 0 && `${req.reservation_items} rez.`}
                                                    {req.reservation_items > 0 && req.order_items > 0 && ' + '}
                                                    {req.order_items > 0 && `${req.order_items} cmd.`}
                                                </span>
                                                <span>
                                                    {req.requested_at ? new Date(req.requested_at).toLocaleString('ro-RO', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : ''}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => handleMarkDone(req.customer_id)}
                                                className="w-full mt-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-all"
                                            >
                                                <CheckCircle size={16} />
                                                Marchez ca procesat
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modification Requests Panel */}
            {showModificationPanel && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-white font-bold text-lg flex items-center gap-2">
                                <Bell size={20} />
                                Cereri Modificare ({modificationRequests.length})
                            </h2>
                            <button
                                onClick={() => setShowModificationPanel(false)}
                                className="text-white/80 hover:text-white p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            {modificationRequests.length === 0 ? (
                                <p className="text-center text-zinc-500 py-8">Nu există cereri de modificare</p>
                            ) : (
                                <div className="space-y-4">
                                    {modificationRequests.map((req) => (
                                        <div key={req.id} className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
                                            <div className="flex items-start justify-between mb-3">
                                                <div>
                                                    <h3 className="font-bold text-zinc-800">
                                                        {req.customer_name || `Client ${req.customer_id}`}
                                                    </h3>
                                                    <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                                                        <Phone size={14} />
                                                        {req.customer_phone || 'Fără telefon'}
                                                    </div>
                                                    {req.box_number && (
                                                        <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                                                            <MapPin size={14} />
                                                            Cutia {req.box_number}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-xs text-zinc-400">
                                                    {req.created_at ? new Date(req.created_at).toLocaleString('ro-RO', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : ''}
                                                </span>
                                            </div>
                                            
                                            {/* Modification Items */}
                                            <div className="space-y-2 mb-3">
                                                {req.items?.map((item, idx) => (
                                                    <div key={idx} className={`p-3 rounded-lg ${item.action === 'remove' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {item.action === 'remove' ? (
                                                                <span className="px-2 py-0.5 bg-red-200 text-red-700 text-xs font-bold rounded">ELIMINĂ</span>
                                                            ) : (
                                                                <span className="px-2 py-0.5 bg-green-200 text-green-700 text-xs font-bold rounded">ADAUGĂ</span>
                                                            )}
                                                            <span className="font-medium text-zinc-800">{item.item_name}</span>
                                                            <span className="text-zinc-500">x{item.quantity}</span>
                                                            {item.barcode && (
                                                                <span className="px-2 py-0.5 bg-zinc-200 text-zinc-600 text-xs font-mono rounded">
                                                                    {item.barcode}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {item.action === 'remove' && item.reason && (
                                                            <p className="text-sm text-red-600 mt-1 ml-1">
                                                                Motiv: {getReasonLabel(item.reason, item.custom_reason)}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            <button
                                                onClick={() => handleMarkModificationDone(req.id)}
                                                className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-all"
                                            >
                                                <CheckCircle size={16} />
                                                Marchez ca procesat
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </RefreshContext.Provider>
    );
};

export default Layout;
